/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { __decorate } from "tslib";
import { html } from 'lit';
import { state, i18n, I18nService, property } from '@cds/core/internal';
import { CdsControl } from '@cds/core/forms';
import styles from './file.element.scss';
/**
 * Time Input
 *
 * ```typescript
 * import '@cds/core/file/register.js';
 * ```
 *
 * ```html
 * <cds-file>
 *   <label>file</label>
 *   <input type="file" />
 * </cds-file>
 * ```
 *
 * @element cds-file
 * @slot - For projecting file input and label
 */
export class CdsFile extends CdsControl {
    constructor() {
        super(...arguments);
        this.i18n = I18nService.keys.file;
        /**
         * Set the label of the browse button.
         */
        this.buttonLabel = this.i18n.browse;
        this.fixedControlWidth = true;
        this.supportsPrefixSuffixActions = false;
    }
    static get styles() {
        return [...super.styles, styles];
    }
    get inputTemplate() {
        return html `
      <div cds-layout="horizontal gap:sm align:vertical-center">
        <cds-button size="sm" action="outline" @click="${() => this.label.click()}" ?disabled=${this.disabled}>
          <cds-icon shape="folder" aria-hidden="true"></cds-icon>
          <span>${this.buttonLabelForSelection || this.buttonLabel}</span>
        </cds-button>
        ${this.clearFilesControlTemplate}
      </div>
    `;
    }
    get clearFilesControlTemplate() {
        return this.inputControl.files?.length && !this.disabled
            ? html `<cds-button-action
          shape="times"
          @click=${() => this.clearFiles()}
          aria-label="${this.i18n.removeFile}"
        ></cds-button-action>`
            : html ``;
    }
    firstUpdated(props) {
        super.firstUpdated(props);
        this.inputControl.addEventListener('change', e => {
            // NOTE: have to distinguish here being user-caused events and programmatic
            // events (e.isTrusted true/false) so that we don't fire a change event loop
            if (e.isTrusted) {
                this.updateLabelAndFocus(e.target.files);
            }
        });
    }
    /** @private */
    clearFiles(fireEvent = true) {
        this.buttonLabelForSelection = '';
        this.inputControl.value = '';
        // when input is reset like this it isn't registering an onchange event
        // NOTE: tsc + karma is complaining about dispatchEvent being a method on inputControl
        if (fireEvent && this.inputControl.dispatchEvent) {
            this.inputControl.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
        }
        const browseButton = this.shadowRoot?.querySelector('cds-button');
        if (browseButton) {
            browseButton.focus();
        }
    }
    /** @private */
    updateLabelAndFocus(files) {
        if (files && files.length) {
            this.buttonLabelForSelection = files.length > 1 ? `${files.length} ${this.i18n.files}` : files[0].name;
        }
        else {
            this.clearFiles(false);
        }
    }
}
__decorate([
    i18n()
], CdsFile.prototype, "i18n", void 0);
__decorate([
    property()
], CdsFile.prototype, "buttonLabel", void 0);
__decorate([
    state()
], CdsFile.prototype, "buttonLabelForSelection", void 0);
__decorate([
    state()
], CdsFile.prototype, "fixedControlWidth", void 0);
__decorate([
    state()
], CdsFile.prototype, "supportsPrefixSuffixActions", void 0);
